<template>
  <div>
    <v-toolbar class="fixed-bar">
      <v-btn
        v-b-tooltip
        class="mr-3"
        title="Back to Library"
        @click="$router.push('/alerts/groups')"
        aria-label="Back to Library"
      >
        <v-icon>mdi-arrow-up</v-icon>
      </v-btn>

      <span v-if="group">
        <span>Group: {{ group.text || group.name }};</span>
        <span class="ml-2">
          Logic:
          <span
            v-if="group.logicType === 'OR'"
            v-b-tooltip
            title="ANY (at least one condition must be met)"
          >
            <v-icon color="green">mdi-gate-or</v-icon>
          </span>
          <span
            v-else-if="group.logicType === 'AND'"
            v-b-tooltip
            title="ALL (all conditions must be met)"
          >
            <v-icon color="blue">mdi-gate-and</v-icon>
          </span>
        </span>
      </span>

      <v-spacer />

      <v-switch
        v-model="showDetails"
        class="mt-5 mr-4"
        label="Details"
      />

      <v-btn-toggle>
        <v-btn
          title="Export to JSON"
          @click="exportFile('JSON')"
          v-b-tooltip
          aria-label="Export to JSON"
        >
          <v-icon>mdi-file-download-outline</v-icon>
        </v-btn>

        <FileUploadDialog
          v-if="group && !group.frozen"
          :upload-url="`${ALERTS_URL}/groups/${this.groupId}/replace`"
          button-title="mdi-file-replace-outline"
          label="JSON file"
          title="Replace from JSON file"
          warning="This will replace content of this group. Are you sure?"
          @uploaded="onFileUploaded"
        />

        <FileUploadDialog
          v-if="group && !group.frozen"
          :preflight-url="`${SERVICES_URL}/preflight`"
          :upload-url="`${ALERTS_URL}/groups/${this.groupId}/add`"
          button-title="mdi-file-upload-outline"
          show-operations
          title="Add operations from SDF, RDF, XLSX or CSV file"
          @uploaded="onFileUploaded"
        />

        <PromptDialog
          v-if="group && !group.frozen"
          v-model="visibleCopy"
          :text="editingItem ? editingItem.value : null"
          :visible="false"
          title="Do you want to copy?"
          @confirmed="copyItem"
        />

        <DeleteDialog
          v-if="group && !group.frozen"
          v-model="visibleDelete"
          :api-url="ALERTS_URL"
          :disabled="!selected.length"
          :items="selected"
          :visible="false"
          button-title="mdi-delete"
          no-server-delete
          title="Delete selected actions"
          @click="deleteItems(selected)"
          @itemDeleted="removeItem"
        />

        <CheckEditor
          v-if="group && !group.frozen"
          v-model="visibleEdit"
          :item="editingItem"
          :on-button-activate="resetEditingItem"
          button-title="mdi-playlist-plus"
          max-width="600"
          title="Add operation"
          @onSave="itemUpdated"
        />

        <v-btn
          :title="group && group.frozen ? 'Thaw group' : 'Freeze group'"
          @click="freezeMeltGroup()"
          :aria-label="group && group.frozen ? 'Thaw group' : 'Freeze group'"
        >
          <v-icon v-if="group && group.frozen">mdi-snowflake-melt</v-icon>
          <v-icon v-else>mdi-snowflake</v-icon>
        </v-btn>
      </v-btn-toggle>
    </v-toolbar>

    <v-data-table
      v-model="selected"
      :footer-props="{
        showFirstLastPage: true,
        itemsPerPageOptions: [10, 30, 50, 100, -1],
      }"
      :headers="headers"
      :hide-default-footer="items.length <= 10"
      :items="items"
      :items-per-page="100"
      :search="search"
      item-key="key"
    >
      <template #[`item.type`]="{ item }">
        {{ item['@c'].substring(1) }}
      </template>

      <template #[`item.logicType`]="{ item }">
        <span
          v-if="item.logicType === 'NOT'"
          v-b-tooltip
          title="NOT (condition must NOT be met)"
        >
          <v-icon color="red">mdi-gate-not</v-icon>
        </span>
      </template>

      <template #[`item.subType`]="{ item }">
        {{ item.type }}
      </template>

      <template #[`item.value`]="{ item }">
        <img
          v-if="item.type === 'SMIRKS'"
          :src="`${ALERTS_URL}/render?smirks=${encodeURIComponent(item.value)}`"
          alt=""
          style="width: 300px; height: 150px"
        />
        <img
          v-else-if="item.type === 'SMARTS' || item.type === 'SMILES'"
          :src="`${ALERTS_URL}/render?smiles=${encodeURIComponent(item.value)}`"
          alt=""
          style="width: 150px; height: 150px"
        />
        <b-link
          v-else-if="item['@c'] === '.GroupReference'"
          :to="`/alerts/groups/${encodeURIComponent(item.value)}`"
        >
          {{ item.value }}
        </b-link>
        <span v-else>{{ item.value }}</span>

        <div v-if="showDetails">
          <img
            v-if="item.type === 'TOXPRINT'"
            :src="`${TOXPRINTS_URL}/render?label=${encodeURIComponent(item.value)}`"
            alt=""
          />
          <div
            v-else-if="['SMIRKS', 'SMILES', 'SMARTS'].includes(item.type)"
            style="font-size: smaller"
          >
            {{ item.value }}
          </div>
        </div>
      </template>

      <template #[`item.title`]="{ item }">
        <b-link
          v-if="item['@c'] === '.GroupReference'"
          :to="`/alerts/groups/${encodeURIComponent(item.value)}`"
        >
          {{ item.title }}
        </b-link>
        <span v-else>{{ item.title }}</span>
      </template>

      <template #[`item.actions`]="{ item }">
        <div v-if="group && !group.frozen">
          <v-icon
            v-if="items.indexOf(item) !== 0"
            class="ma-1"
            title="Move Up"
            @click="moveUp(item)"
          >
            mdi-arrow-up
          </v-icon>
          <v-icon
            v-if="items.indexOf(item) !== items.length - 1"
            class="ma-1"
            title="Move Down"
            @click="moveDown(item)"
          >
            mdi-arrow-down
          </v-icon>
          <v-icon
            class="ma-1"
            title="Edit"
            @click="editItem(item)"
          >
            mdi-playlist-edit
          </v-icon>
          <v-icon
            class="ma-1"
            title="Copy"
            @click="confirmCopyItem(item)"
          >
            mdi-content-copy
          </v-icon>
          <v-icon
            class="ma-1"
            title="Delete"
            @click="deleteItems([item])"
          >
            mdi-delete
          </v-icon>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { ALERTS_URL, download, SERVICES_URL, TOXPRINTS_URL } from '@/main'
import { showErrorToast } from '@/utils/toast'
import DeleteDialog from '@/components/dialogs/DeleteDialog.vue'
import CheckEditor from '@/components/alerts/CheckEditor'
import FileUploadDialog from '@/components/dialogs/FileUploadDialog.vue'
import PromptDialog from '@/components/dialogs/PromptDialog.vue'
import ChecksGroupBase from '@/components/alerts/ChecksGroupBase'

function move(arr, from, to) {
  arr.splice(to, 0, arr.splice(from, 1)[0])
}

export default {
  mixins: [ChecksGroupBase],
  components: { PromptDialog, DeleteDialog, CheckEditor, FileUploadDialog },

  data: () => ({
    group: null,
    showDetails: false,
    allHeaders: [
      { text: 'Class', value: 'type' },
      { text: 'Logic', value: 'logicType' },
      { text: 'Type', value: 'subType' },
      { text: 'Value', value: 'value' },
      { text: 'Title', value: 'text' },
      { text: 'Description', value: 'description' },
      { text: 'Actions', value: 'actions', sortable: false, align: 'end' },
    ],
    showErrorToast,
  }),

  computed: {
    SERVICES_URL() {
      return SERVICES_URL
    },
    TOXPRINTS_URL() {
      return TOXPRINTS_URL
    },
    groupId() {
      return this.$route.params.id
    },
    hasChecks() {
      return !!this.items.find((i) => i['@c'] === '.Check')
    },
    headers() {
      let _headers = this.allHeaders
      if (!this.hasChecks) _headers = _headers.filter((h) => h.value !== 'severity' && h.value !== 'action')
      if (this.group && this.group.frozen) _headers = _headers.filter((h) => h.value !== 'actions')
      return _headers
    },
  },

  watch: {
    groupId() {
      this.getData()
    },
  },

  methods: {
    freezeMeltGroup() {
      this.$axios
        .patch(`${ALERTS_URL}/groups/${this.groupId}/freeze?freeze=${!this.group.frozen}`)
        .then(() => (this.group.frozen = !this.group.frozen))
    },
    removeItem(item) {
      this.items.splice(this.items.indexOf(item), 1)
      this.updateDatabase()
    },
    copyItem() {
      let i = this.items.indexOf(this.editingItem)
      let item = Object.assign({}, this.editingItem)
      item.key = this.maxKey++
      this.items.splice(i + 1, 0, item)
      this.updateDatabase()
    },
    exportFile(format) {
      format = format.toLowerCase()
      this.$axios
        .get(`${ALERTS_URL}/groups/${this.groupId}`, {
          responseType: 'arraybuffer',
        })
        .then((response) => {
          let filename = `${this.group.value}.${format.toLowerCase()}`
          download(filename, response.data, format)
        })
        .catch((error) => {
          console.error('Error during export:', error)
          showErrorToast(error, { title: 'Error during export' })
        })
    },
    moveUp(item) {
      let i = this.items.indexOf(item)
      if (i > 0) {
        move(this.items, i, i - 1)
        this.updateDatabase()
      }
    },
    moveDown(item) {
      let i = this.items.indexOf(item)
      if (i < this.items.length - 1) {
        move(this.items, i, i + 1)
        this.updateDatabase()
      }
    },
    updateDatabase() {
      this.$axios.put(`${ALERTS_URL}/groups/${this.groupId}`, this.group)
    },
    onFileUploaded(group) {
      group.operations.forEach((v, i) => (v.key = i))
      this.group = group
      this.items = this.group.operations
      this.maxKey = this.items.length
    },
    getData() {
      this.$axios.get(`${ALERTS_URL}/groups/${this.groupId}`).then((response) => {
        response.data.operations.forEach((v, i) => (v.key = i))
        this.group = response.data
        this.items = this.group.operations
        this.maxKey = this.items.length
      })
    },
  },
}
</script>

<style scoped></style>
